import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import DataTable from 'components/shared/DataTable';
import Foldout from 'components/shared/Foldout';
import { MultiSelectChips } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import Search from 'components/shared/Search';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { adminClientsQueryKeys, adminShowClient, adminUpdateClient } from 'services/api/admin/clients';
import { colors } from 'styles/theme/colors';
import TrashIcon from 'svg/TrashIcon';

type PlanEntry = { id: string; name: string; type: string };

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const columnHelper = createColumnHelper<PlanEntry>();

type Props = {
  clientId: string;
  clientName: string;
  handleClose: () => void;
};

export default function ClientClassificationsSlideout({ clientId, clientName, handleClose }: Props) {
  const [isAdding, setIsAdding] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState<PlanEntry[]>([]);
  const [localPlans, setLocalPlans] = useState<PlanEntry[]>([]);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const { data: client } = useQuery({
    queryKey: adminClientsQueryKeys.show(clientId),
    queryFn: ({ signal }) => adminShowClient(clientId, signal, ['healthPlanClassifications']),
    enabled: !!clientId,
  });

  useEffect(() => {
    if (client) setSelectedPlans(client.healthPlanClassifications);
  }, [client]);

  const healthPlanOptions = useInfiniteOptions({
    queryKey: ['adminClassifications', 'health_plan', 'unarchived'],
    fetchFn: adminIndexClassifications,
    params: { type: 'health_plan', archived: false, sortBy: 'name asc' },
  });

  const hasChanges = (() => {
    if (!client) return false;
    const a = selectedPlans
      .map((p) => p.id)
      .sort()
      .join(',');
    const b = client.healthPlanClassifications
      .map((p: any) => p.id)
      .sort()
      .join(',');
    return a !== b;
  })();

  const mutation = useMutation({
    mutationFn: () => adminUpdateClient(clientId, { healthPlanClassificationIds: selectedPlans.map((p) => p.id) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminClientsQueryKeys.index() });
      handleClose();
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Plan',
        cell: (info) => <strong>{info.getValue()}</strong>,
      }),
      columnHelper.accessor((row) => row, {
        id: 'remove',
        enableSorting: false,
        header: '',
        size: 40,
        cell: (info) => (
          <TrashContainer onClick={() => setSelectedPlans(selectedPlans.filter((p) => p.id !== info.row.original.id))}>
            <TrashIcon color={colors.red100} width={16} height={16} />
          </TrashContainer>
        ),
      }),
    ],

    [selectedPlans]
  );

  return (
    <Foldout open position='right' onBackdropClick={handleClose} width='min(80%, 480px)'>
      <PanelContainer>
        <PanelHeader>
          <PanelTitle>{clientName} - Contracted Plans</PanelTitle>
          <CloseButton onClick={handleClose}>&times;</CloseButton>
        </PanelHeader>
        <PanelContent>
          {isAdding ? (
            <>
              <FormHeader>Add New Contracted Health Plans</FormHeader>
              <InputGroup>
                <MultiSelectChips
                  title='Contracted Plans'
                  {...healthPlanOptions}
                  values={localPlans}
                  onChange={setLocalPlans}
                  getOptionValue={getId}
                  getOptionLabel={getName}
                  placeholder='Select plans...'
                />
              </InputGroup>
              <FormActions>
                <ButtonGroup>
                  <Button variant='ghost' onClick={() => setIsAdding(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedPlans(localPlans);
                      setIsAdding(false);
                    }}>
                    Save
                  </Button>
                </ButtonGroup>
              </FormActions>
            </>
          ) : (
            <>
              <SearchBar>
                <Search value={search} onChange={setSearch} placeholder='Contracted Health Plans' />
                <Button
                  onClick={() => {
                    setLocalPlans(selectedPlans);
                    setIsAdding(true);
                  }}>
                  Add Contracted Plans
                </Button>
              </SearchBar>
              <Count>{selectedPlans.length} plans</Count>
              <DataTable
                columns={columns}
                data={selectedPlans.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))}
                defaultSortBy={[{ id: 'name', desc: false }]}
              />
            </>
          )}
        </PanelContent>
        <PanelFooter>
          <ButtonGroup>
            <Button onClick={() => mutation.mutate()} disabled={isAdding || !hasChanges || mutation.isPending}>
              Apply
            </Button>
          </ButtonGroup>
        </PanelFooter>
      </PanelContainer>
    </Foldout>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const FormHeader = styled.p`
  font-weight: 500;
  margin: 0 0 20px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  align-items: center;
  justify-content: space-between;
`;

const Count = styled.p`
  font-size: 12px;
  font-weight: 500;
  margin: 0 0 16px;
`;

const TrashContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
`;
